<template>
  <!-- Backdrop (no overlay, don't block clicks behind) -->
<div
  class="fixed inset-0 z-50 flex items-end justify-center md:items-start md:justify-end
         bg-transparent pointer-events-none"
>
  <!-- Panel -->
  <div
    class="w-full max-w-md h-[80vh] md:h-auto md:max-h-[80vh] md:w-80 md:mr-0 md:mt-32
           flex flex-col rounded-lg shadow-2xl pointer-events-auto border-2"
    :style="{
      backgroundColor: 'var(--cart-bg)',
      borderColor: 'var(--primary)'           // gold stroke
    }"
  >
    <!-- Header -->
    <div
      class="px-6 py-4 flex items-center justify-between"
      :style="{ backgroundColor: 'var(--cart-header-bg)', color: 'var(--cart-header-text)' }"
    >
        <div
          class="flex items-center gap-3"
          :class="{ 'flex-row-reverse': $i18n.locale === 'ar' }"
        >
          <!-- English: Icon then Text -->
          <img
            v-if="$i18n.locale !== 'ar'"
            src="@/assets/icons/cart.svg"
            alt="Cart"
            class="w-6 h-6"
            draggable="false"
          />
          <h2 class="text-xl font-bold">{{ $t("cart.title") }}</h2>
          <!-- Arabic: Text then Icon -->
          <img
            v-if="$i18n.locale === 'ar'"
            src="@/assets/icons/cart.svg"
            alt="Cart"
            class="w-6 h-6"
            draggable="false"
          />
        </div>

        <button
          @click="$emit('close')"
          class="hover:opacity-80 transition"
          :style="{ color: 'var(--cart-header-text)' }"
          aria-label="Close cart"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>
      </div>

      <!-- Cart Items -->
      <div class="flex-1 overflow-y-auto px-6 py-4">
        <!-- Empty -->
        <div
          v-if="cart.length === 0"
          class="text-center py-8"
          :style="{ color: 'var(--cart-item-muted)' }"
        >
          <img
            src="@/assets/Order/empty-cart.png"
            alt="Empty Cart"
            class="w-32 h-32 mx-auto mb-4 opacity-50"
            draggable="false"
          />
          <p>{{ $t("cart.empty") }}</p>
        </div>

        <!-- Items -->
        <div v-else class="space-y-4">
          <div
            v-for="(item, index) in cart"
            :key="index"
            class="rounded-lg p-4"
            :style="{
              backgroundColor: 'var(--cart-item-bg)',
              color: 'var(--cart-item-text)'
            }"
          >
            <!-- Item Header -->
            <div class="flex justify-between items-start mb-3">
              <h3 class="font-semibold text-lg leading-snug">
                {{ getTranslatedDishName(item.name, t) }}
              </h3>
              <button
                @click="removeFromCart(index)"
                class="hover:opacity-75 transition"
                :style="{ color: 'var(--cart-item-muted)' }"
                aria-label="Remove item"
              >
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  ></path>
                </svg>
              </button>
            </div>

            <!-- Quantity Controls -->
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-3">
                <button
                  @click="decreaseQuantity(index)"
                  class="w-8 h-8 rounded-full flex items-center justify-center font-bold transition"
                  :style="{
                    backgroundColor: 'var(--primary)',
                    color: 'var(--on-primary)'
                  }"
                  aria-label="Decrease quantity"
                >
                  −
                </button>

                <span class="font-semibold text-lg">{{ item.quantity }}</span>

                <button
                  @click="increaseQuantity(index)"
                  class="w-8 h-8 rounded-full flex items-center justify-center font-bold transition"
                  :style="{
                    backgroundColor: 'var(--primary)',
                    color: 'var(--on-primary)'
                  }"
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>

              <span class="font-semibold text-lg">
                {{
                  $t("cart.currency", {
                    amount: (item.price * item.quantity).toFixed(2),
                  })
                }}
              </span>
            </div>

            <!-- Special Request -->
            <div class="space-y-2">
              <button
                @click="toggleSpecialRequest(index)"
                class="text-sm hover:underline transition"
                :style="{ color: 'var(--primary)' }"
              >
                {{ $t("cart.specialRequest") }}
              </button>

              <div v-if="item.showSpecialRequest" class="mt-2">
                <textarea
                  v-model="item.specialRequest"
                  @input="updateSpecialRequest(index, $event.target.value)"
                  :placeholder="$t('cart.specialRequestPlaceholder')"
                  class="w-full px-3 py-2 rounded-md border focus:outline-none focus:ring-0 resize-none text-sm"
                  rows="2"
                  :style="{
                    backgroundColor: 'var(--cart-input-bg)',
                    color: 'var(--cart-input-text)',
                    borderColor: 'var(--cart-border)'
                  }"
                ></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary + Checkout -->
      <div
        class="p-6 border-t"
        :style="{
          backgroundColor: 'var(--cart-item-bg)',
          borderColor: 'var(--cart-border)',
          color: 'var(--cart-item-text)'
        }"
      >
        <!-- Cost Breakdown -->
        <div class="space-y-2 mb-4">
          <div class="flex justify-between">
            <span :style="{ color: 'var(--cart-item-text)' }">{{ $t("cart.subtotal") }}:</span>
            <span :style="{ color: 'var(--cart-item-text)' }">{{
              $t("cart.currency", { amount: subtotal.toFixed(2) })
            }}</span>
          </div>

          <div class="flex justify-between">
            <span :style="{ color: 'var(--cart-item-text)' }">{{ $t("cart.serviceFee") }}:</span>
            <span :style="{ color: 'var(--cart-item-text)' }">{{
              $t("cart.currency", { amount: serviceFee.toFixed(2) })
            }}</span>
          </div>

          <div v-if="!props.isFromPreorder" class="flex justify-between">
            <span :style="{ color: 'var(--cart-item-text)' }">{{ $t("cart.deliveryFee") }}:</span>
            <span class="text-sm" :style="{ color: 'var(--cart-item-muted)' }">
              {{ $t("cart.calculatedAtCheckout") }}
            </span>
          </div>

          <div v-if="props.isFromPreorder" class="flex justify-between">
            <span :style="{ color: 'var(--cart-item-text)' }">{{ $t("cart.grandTotal") }}:</span>
            <span class="font-semibold" :style="{ color: 'var(--primary)' }">
              {{
                $t("cart.currency", {
                  amount: (subtotal + serviceFee).toFixed(2),
                })
              }}
            </span>
          </div>
        </div>

        <!-- Checkout Button -->
        <button
          @click="handleCheckout"
          :disabled="cart.length === 0"
          class="w-full font-bold py-4 rounded-lg flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
          :style="{
            backgroundColor: 'var(--primary)',
            color: 'var(--on-primary)'
          }"
        >
          {{ props.isFromPreorder ? $t("cart.continue") : $t("cart.checkout") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { getFirestore, doc, updateDoc } from "firebase/firestore";
import { getTranslatedDishName } from "@/utils/dishTranslation";
import { getAuth } from "firebase/auth";

const { t } = useI18n();
const router = useRouter();

const props = defineProps({
  cart: {
    type: Array,
    required: true,
  },
  isFromPreorder: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close", "update-cart", "remove-item"]);

const subtotal = computed(() => {
  return props.cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
});

const serviceFee = computed(() => subtotal.value * 0.15); // 15%

const toggleSpecialRequest = (index) => {
  const updatedCart = [...props.cart];
  updatedCart[index].showSpecialRequest = !updatedCart[index].showSpecialRequest;
  emit("update-cart", updatedCart);
};

const updateSpecialRequest = (index, value) => {
  const updatedCart = [...props.cart];
  updatedCart[index].specialRequest = value;
  emit("update-cart", updatedCart);
};

const increaseQuantity = (index) => {
  const updatedCart = [...props.cart];
  updatedCart[index].quantity += 1;
  emit("update-cart", updatedCart);
};

const decreaseQuantity = (index) => {
  const updatedCart = [...props.cart];
  if (updatedCart[index].quantity > 1) {
    updatedCart[index].quantity -= 1;
  } else {
    updatedCart.splice(index, 1);
  }
  emit("update-cart", updatedCart);
};

const removeFromCart = (index) => {
  const updatedCart = [...props.cart];
  updatedCart.splice(index, 1);
  emit("update-cart", updatedCart);
};

const handleCheckout = async () => {
  if (props.cart.length === 0) return;

  emit("close");

  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    router.push("/login");
    return;
  }

  if (props.isFromPreorder) {
    try {
      const db = getFirestore();
      const reservationId = localStorage.getItem("reservationId");

      if (reservationId) {
        const orderItems = props.cart.map((item) => ({
          dishName: getTranslatedDishName(item.name, t),
          count: item.quantity,
          specialRequest: item.specialRequest || "none",
        }));

        const reservationRef = doc(db, "reservations", reservationId);
        await updateDoc(reservationRef, {
          order: orderItems,
          orderGrandTotal: subtotal.value + serviceFee.value,
          orderCreatedAt: new Date(),
        });
      }

      router.push("/choose-table");
    } catch (error) {
      console.error("Error saving order to reservation:", error);
      router.push("/choose-table"); // still navigate
    }
  } else {
    router.push("/checkout");
  }
};
</script>

<style scoped>
/* No hard-coded colors here; everything comes from CSS variables. */
/* If you want a thinner border on mobile while keeping 2px on desktop: */
@media (max-width: 767px) {
  .border-2 {
    border-width: 1px;
  }
}
</style>
